import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useReploHotkeys } from "@editor/hooks/useHotkeys";

import { useModifiers } from "@/features/react-editor/components/modifiers/hooks/useModifiers";
import { useModifiersStore } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";

interface ReactEditorGlobalHotkeysListenerProps {
  containerRef: React.RefObject<HTMLDivElement>;
}

const ReactEditorGlobalHotkeysListener: React.FC<
  ReactEditorGlobalHotkeysListenerProps
> = ({ containerRef }) => {
  const { selectedElement, displayMode } = useModifiersStore();

  const { applyModifier, isLoading: isPending } = useModifiers();

  const handleModifiersChange = React.useCallback(
    async (modifiers: UniversalModifierValue[]) => {
      for (const modifier of modifiers) {
        await applyModifier(modifier);
      }
    },
    [applyModifier],
  );

  const handleDelete = React.useCallback(() => {
    const isInSelectMode = displayMode === "select";
    const isNotLoading = !isPending;
    const isNotTextElement = selectedElement?.elementType !== "text";

    const canDeleteElement =
      isInSelectMode && selectedElement && isNotLoading && isNotTextElement;

    if (canDeleteElement) {
      void handleModifiersChange([{ type: "delete" }]);
    }
  }, [displayMode, selectedElement, isPending, handleModifiersChange]);

  useReploHotkeys(
    {
      delete: handleDelete,
    },
    containerRef.current,
  );

  // Focus the container when we have a selected element to ensure keyboard events work
  React.useEffect(() => {
    if (displayMode === "select" && selectedElement && containerRef.current) {
      // NOTE (Kurt, 2025-07-17): We need to focus the container element when a modifier element
      // is selected so that keyboard events (like delete/backspace) are properly captured.
      // Without this focus, the events would be captured by iframes or other elements,
      // preventing the delete hotkey from working until the user manually clicks somewhere.
      containerRef.current.focus();
    }
  }, [displayMode, selectedElement, containerRef]);

  return null;
};

export default ReactEditorGlobalHotkeysListener;
